import { TrialWidget } from 'components/TrialWidget'
import { useContext, useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router'
import { ComposerOverlayContext } from 'routes/ComposerOverlayContext'
import ContentSelectionDrawer from 'routes/find/components/ContentSelectionDrawer'
import AppNavigationSidebar from '../AppNavigationSidebar'
import CalloutManager from '../CalloutManager/CalloutManager'
import styles from './AppLayout.pcss'

interface AppLayoutProps {
  hidden?: boolean
  blurred?: boolean
  hideSidebar?: boolean
}

export function AppLayout(props: AppLayoutProps) {
  const { hidden, blurred, hideSidebar } = props
  const location = useLocation()
  const composerOverlay = useContext(ComposerOverlayContext)
  const isHomepage = location.pathname.startsWith('/home')
  const isComposer = composerOverlay || location.pathname.startsWith('/composer')
  const scrollingElement = useRef<HTMLDivElement>(null)

  // Scroll down/up one screen on Spacebar (Shift + Spacebar) press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }
      if (event.key === ' ' && scrollingElement.current) {
        const scrollAmount = event.shiftKey ? -window.innerHeight : window.innerHeight
        scrollingElement.current.scrollBy({
          top: scrollAmount,
          behavior: 'smooth'
        })
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const containerClassName = [
    styles['app-container'],
    blurred ? styles.blur : '',
    hideSidebar ? styles['no-sb'] : '',
    isHomepage ? styles['nav-collapsed'] : ''
  ].filter(Boolean).join(' ')

  return (
    <section className={containerClassName}>
      <main className={styles.main} data-test="main" data-testid="main" ref={scrollingElement}>
        <div className={`${styles.layout} ${hidden ? styles.hidden : ''}`}>
          <div className={styles['side-panel']}>
            <AppNavigationSidebar />
          </div>
          <div className={styles.content}>
            <CalloutManager />
            <ContentSelectionDrawer className={`${styles['content-selection-drawer']} ${isComposer ? styles.flushLeft : ''}`} />
            <Outlet />
          </div>
        </div>
      </main>
      <TrialWidget />
    </section>
  )
}
